import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ReminderService } from '../reminder/reminder.service';
import { WishesService } from './wishes.service';
import { WishesTrackComponent } from './wishes-track.component';
import { ToastService } from '../../services/toast.service';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import {
  BED_TIME_OPTIONS,
  GOAL_PILLAR_OPTIONS,
  GOALS_BY_PILLAR,
  type GoalPillarKey,
  type WishGoalFieldOption,
  isWishBedTimeGoal,
  isWishFastingGlucoseGoal,
  wishGoalFieldComponent,
  wishGoalFieldUnit,
} from './wish-goal-options';

type Frequency = 'daily' | 'weekly' | '';

export const HABIT_FREQUENCY_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekdays', label: 'Weekdays' },
  { value: 'weekends', label: 'Weekends' },
  { value: 'weekly', label: 'Weekly' },
] as const;

export type HabitFrequency = (typeof HABIT_FREQUENCY_OPTIONS)[number]['value'] | '';

export type WeekdayKey =
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday';

type ReminderCategoryApiItem = {
  _id: string;
  habitName: string;
  subCategories?: Array<{
    name: string;
    predefinedReminderNames?: Array<{
      nameId: number;
      categoryName: string;
      description?: string;
      time?: string;
    }>;
  }>;
};

type ReminderNameOption = { nameId: number; label: string };

/** Action item as returned by GET member-wishes (union of goal, habit, mood). */
export type MemberWishViewAction = {
  actionId?: string;
  goalPillar?: string;
  goal?: string;
  goalValue?: string | number;
  goalUnit?: string;
  goalDescription?: string;
  goalFrequency?: string;
  goalWeekdays?: string[];
  nameId?: number;
  title?: string;
  Description?: string;
  CategoryId?: string;
  habitFrequency?: string;
  habitTime?: string | string[];
  habitJourneyDescription?: string;
  weeklyApplied?: number[];
  habitWeekdays?: string[];
  moodCheckInName?: string;
  moodCheckInDescription?: string;
  frequency?: string;
  daysOfWeek?: string[];
  startDate?: string;
  endDate?: string;
};

export type MemberWishView = {
  id?: string;
  memberId?: string;
  wishName?: string;
  wishDescription?: string;
  /** Wish length in weeks from API (preferred over inferring from dates). */
  wishDuration?: number | string;
  cardForApp?: string;
  startDate?: string;
  endDate?: string;
  actions?: MemberWishViewAction[];
  isCompleted?: boolean;
  isArchived?: boolean;
  isEnrolled?: boolean;
  isTemplateCreated?: boolean;
  careTeamNotes?: string;
  weeklyNotes?: Array<{ notesId?: string; week?: number; notes?: string }>;
  createdDateTime?: string;
  enrolledDateTime?: string;
  modifiedDateTime?: string;
};

export type WishActionSlot =
  | { type: 'select' }
  | {
      type: 'goal';
      /** Present when loaded from an existing wish action (update API). */
      actionId?: string;
      goalPillar: GoalPillarKey | null;
      /** Matches goal key from Goals tab / goalSections (e.g. nutritionScore) */
      goalFieldKey: string | null;
      /** Target value only; unit from wish-goal-options / goal.component */
      goalValue: string | number;
      goalDescription: string;
      frequency: Frequency;
      /** Used when frequency is weekly; click chips to add/remove days */
      weeklyWeekdays: WeekdayKey[];
    }
  | {
      type: 'habit';
      /** Present when loaded from an existing wish action (update API). */
      actionId?: string;
      habitNameId: string | null;
      nameId: number | null;
      frequency: HabitFrequency;
      /** HH:mm entries for each time field; sent as habitTime array. */
      habitTime: string[];
      habitJourneyDescription: string;
      /** Week numbers (1-based) this habit applies to; sent as weeklyApplied in payload. */
      weeklyApplied: number[];
      /** UI-only: true when "All weeks" chip is active. */
      allWeeksApplied: boolean;
      weeklyWeekdays: WeekdayKey[];
      /** UI-only: true after Habit Name/Category changed while a description was already entered. */
      habitDescriptionMismatchWarning?: boolean;
    };

export const CARD_FOR_APP_OPTIONS = [
  'wishCycleBG',
  'wishFlowerBG',
  'wishWalkBG',
  'wishTreeRoadBG',
  'wishFruitsBG',
  'wishFamilyBG',
  'wishMountainBG',
] as const;

export type CardForAppKey = (typeof CARD_FOR_APP_OPTIONS)[number];

export type WishWeeklyNote = {
  week: number;
  notes: string;
  /** Set when loaded from an existing wish (update API). */
  notesId?: string;
};

export type WishFormModel = {
  wishName: string;
  wishDescription: string;
  careTeamNotes: string;
  weeklyNotes: WishWeeklyNote[];
  wishNumberOfWeeks: 1 | 2 | 3 | 4 | 5 | 6 | null;
  cardForApp: CardForAppKey | null;
  actionSlots: WishActionSlot[];
  /** True when the underlying wish is already enrolled; freezes duration/goal edits and past weeks. */
  isEnrolled: boolean;
  enrolledDateTime: string;
};

@Component({
  selector: 'app-wishes',
  standalone: true,
  imports: [CommonModule, FormsModule, WishesTrackComponent, NgxMaterialTimepickerModule],
  templateUrl: './wishes.component.html',
  styleUrl: './wishes.component.css',
})
export class WishesComponent implements OnInit, OnChanges {
  @Input() memberId: string = '';

  activeSubTab: 'create' | 'view' | 'templates' | 'track' = 'create';

  memberWishes: MemberWishView[] = [];
  isLoadingMemberWishes = false;
  memberWishTemplates: MemberWishView[] = [];
  isLoadingMemberWishTemplates = false;
  /** Wish id currently being saved as a template (view tab). */
  savingTemplateWishId: string | null = null;
  /** Wish id currently being archived (view tab). */
  archivingWishId: string | null = null;
  /** Expanded wish cards on the View wishes tab (key from viewWishCardKey). */
  private expandedViewWishKeys = new Set<string>();
  /** Expanded wish cards on the Templates tab (key from templateWishCardKey). */
  private expandedTemplateWishKeys = new Set<string>();

  createForm: WishFormModel = this.emptyWishForm();
  editForm: WishFormModel = this.emptyWishForm();
  editWishModalVisible = false;
  editingWishId: string | null = null;
  /** Snapshot of edit form when modal opened (for partial update diff). */
  private editFormOriginal: WishFormModel | null = null;
  editWishSubmitting = false;

  reminderCategories: ReminderCategoryApiItem[] = [];
  isLoadingReminderCategories = false;
  wishSubmitting = false;

  readonly goalPillarOptions = GOAL_PILLAR_OPTIONS;
  readonly cardForAppOptions = CARD_FOR_APP_OPTIONS;
  readonly bedTimeOptions = BED_TIME_OPTIONS;
  readonly isWishBedTimeGoal = isWishBedTimeGoal;
  readonly isWishFastingGlucoseGoal = isWishFastingGlucoseGoal;

  readonly habitFrequencyOptions = HABIT_FREQUENCY_OPTIONS;

  readonly weekdayOptions: Array<{ key: WeekdayKey; label: string; shortLabel: string }> = [
    { key: 'sunday', label: 'Sunday', shortLabel: 'Sun' },
    { key: 'monday', label: 'Monday', shortLabel: 'Mon' },
    { key: 'tuesday', label: 'Tuesday', shortLabel: 'Tue' },
    { key: 'wednesday', label: 'Wednesday', shortLabel: 'Wed' },
    { key: 'thursday', label: 'Thursday', shortLabel: 'Thu' },
    { key: 'friday', label: 'Friday', shortLabel: 'Fri' },
    { key: 'saturday', label: 'Saturday', shortLabel: 'Sat' },
  ];

  constructor(
    private reminderService: ReminderService,
    private wishesService: WishesService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.loadReminderCategories();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['memberId'] && !changes['memberId'].firstChange) {
      if (this.activeSubTab === 'view') {
        this.loadMemberWishes();
      } else if (this.activeSubTab === 'templates') {
        this.loadMemberWishTemplates();
      }
    }
  }

  setSubTab(tab: 'create' | 'view' | 'templates' | 'track') {
    this.activeSubTab = tab;
    if (tab === 'view') {
      this.loadMemberWishes();
    } else if (tab === 'templates') {
      this.loadMemberWishTemplates();
    }
  }

  formatWishLoggedAt(iso: string | undefined): string {
    if (!iso?.trim()) {
      return '—';
    }
    const d = new Date(iso.trim());
    if (Number.isNaN(d.getTime())) {
      return iso.trim();
    }
    return formatDate(d, 'MMMM d, y, h:mm a', 'en-US');
  }

  /** Completed when today (local calendar day) is after the wish end date. */
  isWishCompletedByEndDate(w: MemberWishView): boolean {
    const endRaw = w?.endDate?.trim();
    if (!endRaw) {
      return false;
    }
    const end = new Date(endRaw);
    if (Number.isNaN(end.getTime())) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return today.getTime() > end.getTime();
  }

  /** In progress when enrolled, not archived, and the end date has not passed. */
  isWishInProgressForView(w: MemberWishView): boolean {
    return !!w?.isEnrolled && !w?.isArchived && !this.isWishCompletedByEndDate(w);
  }

  /** Show “Save as template” only when the API explicitly reports not yet created. */
  showSaveAsTemplateButton(w: MemberWishView): boolean {
    return w?.isTemplateCreated === false;
  }

  isSavingWishTemplate(w: MemberWishView): boolean {
    const id = (w?.id ?? '').toString().trim();
    return Boolean(id) && this.savingTemplateWishId === id;
  }

  saveWishAsTemplate(w: MemberWishView): void {
    const wishId = (w?.id ?? '').toString().trim();
    if (!wishId) {
      this.toast.errorMessage('Wish id is missing.');
      return;
    }
    if (this.savingTemplateWishId) {
      return;
    }
    this.savingTemplateWishId = wishId;
    this.wishesService.saveWishAsTemplate(wishId).subscribe({
      next: (res: any) => {
        this.savingTemplateWishId = null;
        w.isTemplateCreated = true;
        this.toast.successMessage(this.getApiMessage(res) ?? 'Wish saved as template.');
      },
      error: (err: any) => {
        this.savingTemplateWishId = null;
        this.toast.errorMessage(this.getApiMessage(err) ?? 'Failed to save wish as template.');
      },
    });
  }

  /** Show “Save as archived” only when the wish is not archived. */
  showSaveAsArchivedButton(w: MemberWishView): boolean {
    return w?.isArchived === false;
  }

  isArchivingWish(w: MemberWishView): boolean {
    const id = (w?.id ?? '').toString().trim();
    return Boolean(id) && this.archivingWishId === id;
  }

  saveWishAsArchived(w: MemberWishView): void {
    this.setWishArchived(w, true);
  }

  /** Show “Remove from Archive” only when the wish is archived. */
  showRemoveFromArchiveButton(w: MemberWishView): boolean {
    return w?.isArchived === true;
  }

  removeWishFromArchive(w: MemberWishView): void {
    this.setWishArchived(w, false);
  }

  private setWishArchived(w: MemberWishView, isArchived: boolean): void {
    const wishId = (w?.id ?? '').toString().trim();
    if (!wishId) {
      this.toast.errorMessage('Wish id is missing.');
      return;
    }
    if (this.archivingWishId) {
      return;
    }
    this.archivingWishId = wishId;
    this.wishesService.updateWish(wishId, { isArchived }).subscribe({
      next: (res: any) => {
        this.archivingWishId = null;
        w.isArchived = isArchived;
        this.toast.successMessage(
          this.getApiMessage(res) ??
            (isArchived ? 'Wish saved as archived.' : 'Wish removed from archive.')
        );
      },
      error: (err: any) => {
        this.archivingWishId = null;
        this.toast.errorMessage(
          this.getApiMessage(err) ??
            (isArchived ? 'Failed to archive wish.' : 'Failed to remove wish from archive.')
        );
      },
    });
  }

  viewWishCardKey(w: MemberWishView, index: number): string {
    const id = (w.id ?? '').toString().trim();
    if (id) return id;
    return `wish-${index}-${w.createdDateTime ?? ''}-${w.wishName ?? ''}`;
  }

  isViewWishCardExpanded(w: MemberWishView, index: number): boolean {
    return this.expandedViewWishKeys.has(this.viewWishCardKey(w, index));
  }

  toggleViewWishCardExpanded(w: MemberWishView, index: number): void {
    const key = this.viewWishCardKey(w, index);
    if (this.expandedViewWishKeys.has(key)) {
      this.expandedViewWishKeys.delete(key);
    } else {
      this.expandedViewWishKeys.add(key);
    }
  }

  templateWishCardKey(w: MemberWishView, index: number): string {
    const id = (w.id ?? '').toString().trim();
    if (id) return `tpl-${id}`;
    return `tpl-${index}-${w.createdDateTime ?? ''}-${w.wishName ?? ''}`;
  }

  isTemplateWishCardExpanded(w: MemberWishView, index: number): boolean {
    return this.expandedTemplateWishKeys.has(this.templateWishCardKey(w, index));
  }

  toggleTemplateWishCardExpanded(w: MemberWishView, index: number): void {
    const key = this.templateWishCardKey(w, index);
    if (this.expandedTemplateWishKeys.has(key)) {
      this.expandedTemplateWishKeys.delete(key);
    } else {
      this.expandedTemplateWishKeys.add(key);
    }
  }

  /** Total wish length in weeks, from API duration if present, else inferred from start/end dates. */
  private wishTotalWeeksForView(w: MemberWishView): number {
    const fromApi = w?.wishDuration;
    if (fromApi != null && fromApi !== '') {
      const n =
        typeof fromApi === 'number' && Number.isFinite(fromApi)
          ? Math.floor(fromApi)
          : parseInt(String(fromApi).trim(), 10);
      if (Number.isFinite(n) && n >= 1) {
        return n;
      }
    }
    const start = w?.startDate ? new Date(w.startDate) : null;
    const end = w?.endDate ? new Date(w.endDate) : null;
    if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return 0;
    }
    const ms = end.getTime() - start.getTime();
    return Math.max(1, Math.round(ms / (7 * 24 * 60 * 60 * 1000)));
  }

  wishDurationWeeksLabel(w: MemberWishView): string {
    const n = this.wishTotalWeeksForView(w);
    return n >= 1 ? (n === 1 ? '1 week' : `${n} weeks`) : '—';
  }

  viewGoalValueLine(a: MemberWishViewAction): string {
    const v = (a?.goalValue ?? '').toString().trim();
    const u = (a?.goalUnit ?? '').toString().trim();
    if (!v && !u) {
      return '';
    }
    if (v && u) {
      return `Value = ${v} ${u}`;
    }
    return v ? `Value = ${v}` : `Value = ${u}`;
  }

  isViewMoodAction(a: MemberWishViewAction): boolean {
    const n = a?.moodCheckInName;
    return typeof n === 'string' && n.trim().length > 0;
  }

  isViewHabitAction(a: MemberWishViewAction): boolean {
    if (this.isViewMoodAction(a)) {
      return false;
    }
    return a?.nameId != null && typeof a?.title === 'string' && a.title.trim().length > 0;
  }

  isViewGoalAction(a: MemberWishViewAction): boolean {
    if (this.isViewMoodAction(a) || this.isViewHabitAction(a)) {
      return false;
    }
    const pillar = (a?.goalPillar ?? '').toString().trim();
    const goal = (a?.goal ?? '').toString().trim();
    return Boolean(pillar && goal);
  }

  formatViewActionFrequency(a: MemberWishViewAction): string {
    const isHabit = this.isViewHabitAction(a);
    const raw = isHabit
      ? (a.habitFrequency ?? a.frequency ?? '').toString().trim().toLowerCase()
      : (a.goalFrequency ?? a.frequency ?? '').toString().trim().toLowerCase();
    const label = this.frequencyDisplayLabel(raw, isHabit);
    if (raw !== 'weekly') {
      return label;
    }
    const daySource = isHabit
      ? (a.habitWeekdays ?? a.daysOfWeek)
      : (a.goalWeekdays ?? a.daysOfWeek);
    const days = this.formatDaysOfWeekList(daySource);
    return days ? `${label} [${days}]` : label;
  }

  habitActionDescription(a: MemberWishViewAction): string {
    const d = a?.Description ?? (a as { description?: string })?.description;
    return typeof d === 'string' ? d.trim() : '';
  }

  /** Returns habitJourneyDescription if present, otherwise falls back to Description. */
  habitViewDescription(a: MemberWishViewAction): string {
    const journey = (a?.habitJourneyDescription ?? '').toString().trim();
    if (journey) return journey;
    return this.habitActionDescription(a);
  }

  /** Week numbers 1..N derived from wishDuration (or start/end dates as fallback). */
  wishViewWeekNumbers(w: MemberWishView): number[] {
    const fromApi = w?.wishDuration;
    if (fromApi != null && fromApi !== '') {
      const n =
        typeof fromApi === 'number' && Number.isFinite(fromApi)
          ? Math.floor(fromApi)
          : parseInt(String(fromApi).trim(), 10);
      if (Number.isFinite(n) && n >= 1) {
        return Array.from({ length: n }, (_, i) => i + 1);
      }
    }
    const start = w?.startDate ? new Date(w.startDate) : null;
    const end = w?.endDate ? new Date(w.endDate) : null;
    if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return [];
    }
    const ms = end.getTime() - start.getTime();
    const weeks = Math.max(1, Math.round(ms / (7 * 24 * 60 * 60 * 1000)));
    return Array.from({ length: weeks }, (_, i) => i + 1);
  }

  /** Badge label for which weeks a habit applies to: "All weeks", "Week 1, 3", etc. */
  habitWeeksAppliedLabel(w: MemberWishView, a: MemberWishViewAction): string {
    const applied = a?.weeklyApplied;
    if (!Array.isArray(applied) || applied.length === 0) return '';
    const sorted = [...applied].sort((x, y) => x - y);
    const totalWeeks = this.wishViewWeekNumbers(w).length;
    if (
      totalWeeks > 0 &&
      sorted.length >= totalWeeks &&
      Array.from({ length: totalWeeks }, (_, i) => i + 1).every((n) => sorted.includes(n))
    ) {
      return 'All weeks';
    }
    return sorted.length === 1 ? `Week ${sorted[0]}` : `Weeks ${sorted.join(', ')}`;
  }

  formatViewHabitTime(a: MemberWishViewAction): string {
    const raw = a?.habitTime;
    if (raw == null) {
      return '';
    }
    const parts = Array.isArray(raw)
      ? raw.map((t) => String(t).trim()).filter(Boolean)
      : String(raw)
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean);
    return parts
      .map((t) => this.firstTimeForTimeInput(t) || t)
      .filter(Boolean)
      .join(', ');
  }

  get habitItems(): Array<{ id: string; name: string }> {
    return (this.reminderCategories ?? [])
      .map((x) => ({ id: x?._id, name: (x?.habitName ?? '').trim() }))
      .filter((x) => Boolean(x.id) && Boolean(x.name));
  }

  categoryOptionsForHabit(habitId: string | null): ReminderNameOption[] {
    if (!habitId) return [];
    const habit = (this.reminderCategories ?? []).find((x) => x?._id === habitId);
    const raw =
      habit?.subCategories?.flatMap((sc) =>
        (sc?.predefinedReminderNames ?? []).map((n) => ({
          nameId: n?.nameId,
          label: (n?.categoryName ?? '').trim(),
        }))
      ) ?? [];

    const options = raw.filter(
      (x) => typeof x.nameId === 'number' && Number.isFinite(x.nameId) && Boolean(x.label)
    ) as ReminderNameOption[];

    const seen = new Set<number>();
    return options.filter((x) => {
      if (seen.has(x.nameId)) return false;
      seen.add(x.nameId);
      return true;
    });
  }

  emptyWishForm(): WishFormModel {
    return {
      wishName: '',
      wishDescription: '',
      careTeamNotes: '',
      weeklyNotes: [],
      wishNumberOfWeeks: null,
      cardForApp: null,
      actionSlots: [{ type: 'select' }],
      isEnrolled: false,
      enrolledDateTime: '',
    };
  }

  openEditWishModal(w: MemberWishView): void {
    const wishId = (w?.id ?? '').toString().trim();
    if (!wishId) {
      this.toast.errorMessage('Wish id is missing.');
      return;
    }
    this.editingWishId = wishId;
    this.applyWishToForm(w, this.editForm);
    this.editFormOriginal = this.cloneWishForm(this.editForm);
    this.editWishModalVisible = true;
  }

  closeEditWishModal(): void {
    this.editWishModalVisible = false;
    this.editingWishId = null;
    this.editFormOriginal = null;
    this.editForm = this.emptyWishForm();
    this.editWishSubmitting = false;
  }

  isEditWishFormDirty(): boolean {
    if (!this.editWishModalVisible || !this.editFormOriginal) {
      return false;
    }
    return (
      this.serializeWishForm(this.editForm) !== this.serializeWishForm(this.editFormOriginal)
    );
  }

  /** True when all wish form fields and every goal/habit action are valid (no toasts). */
  isWishFormComplete(form: WishFormModel, options?: { requireCardForApp?: boolean }): boolean {
    if (!(form.wishName ?? '').trim()) {
      return false;
    }
    if (!(form.wishDescription ?? '').trim()) {
      return false;
    }
    if (!(form.careTeamNotes ?? '').trim()) {
      return false;
    }
    const weeks = form.wishNumberOfWeeks;
    if (weeks == null) {
      return false;
    }
    if (options?.requireCardForApp && !form.cardForApp) {
      return false;
    }

    const weeklyNotes = form.weeklyNotes ?? [];
    if (weeklyNotes.length !== weeks) {
      return false;
    }
    if (weeklyNotes.some((n) => !(n?.notes ?? '').trim())) {
      return false;
    }

    const actions = this.realActionSlots(form);
    if (actions.length === 0) {
      return false;
    }

    return actions.every((slot) => this.isWishActionSlotComplete(slot));
  }

  private isWishActionSlotComplete(
    slot: Exclude<WishActionSlot, { type: 'select' }>
  ): boolean {
    if (slot.type === 'goal') {
      return this.isGoalActionSlotComplete(slot);
    }
    return this.isHabitActionSlotComplete(slot);
  }

  private isGoalActionSlotComplete(
    s: Extract<WishActionSlot, { type: 'goal' }>
  ): boolean {
    if (!s.frequency || !s.goalPillar || !s.goalFieldKey) {
      return false;
    }
    if (!wishGoalFieldComponent(s.goalFieldKey)) {
      return false;
    }
    const gv = s.goalValue;
    if (gv == null || (typeof gv === 'string' ? !gv.trim() : !Number.isFinite(gv))) {
      return false;
    }
    if (s.frequency === 'weekly' && !this.hasWeeklyWeekdaysSelected(s.weeklyWeekdays)) {
      return false;
    }
    return true;
  }

  private isHabitActionSlotComplete(
    s: Extract<WishActionSlot, { type: 'habit' }>
  ): boolean {
    if (!s.frequency || !s.habitNameId || s.nameId == null) {
      return false;
    }
    if (!this.findHabitPredefinedDetails(s.habitNameId, s.nameId)) {
      return false;
    }
    if ((s.habitTime ?? []).length === 0 || s.habitTime.some((t) => !t.trim())) {
      return false;
    }
    if ((s.weeklyApplied ?? []).length === 0) {
      return false;
    }
    if (s.frequency === 'weekly' && !this.hasWeeklyWeekdaysSelected(s.weeklyWeekdays)) {
      return false;
    }
    return true;
  }

  private hasWeeklyWeekdaysSelected(weekdays: WeekdayKey[] | undefined): boolean {
    return this.sortWeekdayKeys(weekdays ?? []).length > 0;
  }

  updateWish(): void {
    if (
      !this.isEditWishFormDirty() ||
      this.editWishSubmitting ||
      !this.isWishFormComplete(this.editForm, { requireCardForApp: true })
    ) {
      return;
    }
    const wishId = (this.editingWishId ?? '').toString().trim();
    if (!wishId) {
      this.toast.errorMessage('Wish id is missing.');
      return;
    }
    const mid = (this.memberId ?? '').toString().trim();
    if (!mid) {
      this.toast.errorMessage('Member is required.');
      return;
    }
    if (this.editForm.wishNumberOfWeeks == null) {
      this.toast.errorMessage('Please select number of weeks.');
      return;
    }
    if (!this.editFormOriginal) {
      return;
    }

    const payload = this.buildUpdateWishApiPayload(this.editForm, this.editFormOriginal);
    if (!payload) {
      return;
    }

    this.editWishSubmitting = true;
    this.wishesService.updateWish(wishId, payload).subscribe({
      next: (res) => {
        this.editWishSubmitting = false;
        this.toast.successMessage(this.getApiMessage(res) ?? 'Wish updated successfully.');
        this.closeEditWishModal();
        this.loadMemberWishes();
      },
      error: (err) => {
        this.editWishSubmitting = false;
        this.toast.errorMessage(this.getApiMessage(err) ?? 'Failed to update wish. Please try again.');
      },
    });
  }

  private serializeWishForm(form: WishFormModel): string {
    return JSON.stringify(form);
  }

  private cloneWishForm(form: WishFormModel): WishFormModel {
    return JSON.parse(this.serializeWishForm(form)) as WishFormModel;
  }

  private realActionSlots(
    form: WishFormModel
  ): Array<Exclude<WishActionSlot, { type: 'select' }>> {
    return form.actionSlots.filter(
      (s): s is Exclude<WishActionSlot, { type: 'select' }> => s.type !== 'select'
    );
  }

  onWishNumberOfWeeksChange(form: WishFormModel): void {
    this.syncWeeklyNotesForWeeks(form);
    this.syncHabitWeeklyApplied(form);
  }

  /** Ensures `weeklyNotes` has one entry per week (1…N), preserving existing text when weeks change. */
  syncWeeklyNotesForWeeks(form: WishFormModel): void {
    const max = form.wishNumberOfWeeks;
    if (max == null) {
      form.weeklyNotes = [];
      return;
    }
    const existing = new Map(
      (form.weeklyNotes ?? []).map((n) => [n.week, n])
    );
    const next: WishWeeklyNote[] = [];
    for (let w = 1; w <= max; w++) {
      const prev = existing.get(w);
      next.push({
        week: w,
        notes: (prev?.notes ?? '').toString(),
        notesId: prev?.notesId,
      });
    }
    form.weeklyNotes = next;
  }

  private syncHabitWeeklyApplied(form: WishFormModel): void {
    const n = form.wishNumberOfWeeks ?? 0;
    let changed = false;
    const next = form.actionSlots.map((slot) => {
      if (slot.type !== 'habit') return slot;
      if (slot.allWeeksApplied) {
        const newApplied = Array.from({ length: n }, (_, j) => j + 1);
        changed = true;
        return { ...slot, weeklyApplied: newApplied };
      }
      const filtered = slot.weeklyApplied.filter((w) => w >= 1 && w <= n);
      if (filtered.length !== slot.weeklyApplied.length) {
        changed = true;
        return { ...slot, weeklyApplied: filtered };
      }
      return slot;
    });
    if (changed) {
      form.actionSlots = next;
    }
  }

  getWeekRange(form: WishFormModel): number[] {
    const n = form.wishNumberOfWeeks ?? 0;
    return Array.from({ length: n }, (_, i) => i + 1);
  }

  weekOrdinalLabel(week: number): string {
    return `${week}${this.ordinalSuffix(week)} week only`;
  }

  private ordinalSuffix(n: number): string {
    const v = n % 100;
    const suffixes = ['th', 'st', 'nd', 'rd'];
    return v >= 11 && v <= 13 ? 'th' : (suffixes[n % 10] ?? 'th');
  }

  /** Label like "2nd week is Ongoing for this wish Journey", shown in the edit modal title when enrolled. */
  ongoingWeekLabel(form: WishFormModel): string {
    const week = this.enrolledWeeksElapsed(form);
    if (week <= 0) {
      return '';
    }
    return `${week}${this.ordinalSuffix(week)} week is Ongoing for this wish Journey`;
  }

  /** Label like "4th week is ongoing", shown after the wish name on the View wishes cards when enrolled. */
  viewOngoingWeekLabel(w: MemberWishView): string {
    const totalWeeks = this.wishTotalWeeksForView(w);
    const week = this.enrolledWeeksElapsedRaw(w?.isEnrolled, w?.enrolledDateTime, totalWeeks);
    if (week <= 0) {
      return '';
    }
    return `${week}${this.ordinalSuffix(week)} week is ongoing`;
  }

  isAllWeeksApplied(form: WishFormModel, index: number): boolean {
    const slot = form.actionSlots[index];
    return slot.type === 'habit' && slot.allWeeksApplied;
  }

  /**
   * Number of weeks (1-based) already begun as of today, counted from enrolledDateTime.
   * A week that has only partially elapsed still counts as begun (freezes it).
   */
  private enrolledWeeksElapsed(form: WishFormModel): number {
    return this.enrolledWeeksElapsedRaw(form.isEnrolled, form.enrolledDateTime, form.wishNumberOfWeeks ?? 0);
  }

  private enrolledWeeksElapsedRaw(isEnrolled: boolean | undefined, enrolledDateTime: string | undefined, totalWeeks: number): number {
    if (!isEnrolled) {
      return 0;
    }
    const raw = (enrolledDateTime ?? '').toString().trim();
    if (!raw) {
      return 0;
    }
    const enrolled = new Date(raw);
    if (Number.isNaN(enrolled.getTime())) {
      return 0;
    }
    enrolled.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((today.getTime() - enrolled.getTime()) / (24 * 60 * 60 * 1000));
    if (diffDays < 0) {
      return 0;
    }
    const elapsed = Math.floor(diffDays / 7) + 1;
    return totalWeeks > 0 ? Math.min(elapsed, totalWeeks) : elapsed;
  }

  /** True when this week number is already begun/passed since enrollment and must stay frozen. */
  isWeekFrozenByEnrollment(form: WishFormModel, week: number): boolean {
    return week <= this.enrolledWeeksElapsed(form);
  }

  /**
   * True when this habit action must be locked to view-only: the API response it was loaded
   * with already applied it to every week, or it already has a past (begun/passed) week applied.
   * A live/in-session "All weeks" toggle does NOT freeze it by itself — only the original
   * API-loaded state does.
   */
  isHabitActionFrozen(form: WishFormModel, slot: WishActionSlot): boolean {
    if (slot.type !== 'habit') {
      return false;
    }
    if (this.isHabitActionAllWeeksFromApi(form, slot)) {
      return true;
    }
    return slot.weeklyApplied.some((w) => this.isWeekFrozenByEnrollment(form, w));
  }

  /** True when this habit action's original API-loaded weeklyApplied already covered every week. */
  isHabitActionAllWeeksFromApi(form: WishFormModel, slot: WishActionSlot): boolean {
    if (slot.type !== 'habit' || form !== this.editForm || !this.editFormOriginal) {
      return false;
    }
    const actionId = (slot.actionId ?? '').trim();
    if (!actionId) {
      return false;
    }
    const original = this.editFormOriginal.actionSlots.find(
      (s): s is Extract<WishActionSlot, { type: 'habit' }> =>
        s.type === 'habit' && (s.actionId ?? '').trim() === actionId
    );
    return !!original?.allWeeksApplied;
  }

  toggleAllWeeksApplied(form: WishFormModel, index: number): void {
    const slot = form.actionSlots[index];
    if (slot.type !== 'habit') return;
    /** Locked for regular actions while enrolled, except an action already applied to
     *  every week per the API — that one may still be toggled to customize future weeks. */
    if (form.isEnrolled && !this.isHabitActionAllWeeksFromApi(form, slot)) return;
    const n = form.wishNumberOfWeeks ?? 0;
    if (slot.allWeeksApplied) {
      /** Past weeks already happened — keep them applied and frozen, only future weeks are cleared. */
      const keepFrozen = Array.from({ length: this.enrolledWeeksElapsed(form) }, (_, j) => j + 1);
      form.actionSlots[index] = { ...slot, allWeeksApplied: false, weeklyApplied: keepFrozen };
    } else {
      form.actionSlots[index] = {
        ...slot,
        allWeeksApplied: true,
        weeklyApplied: Array.from({ length: n }, (_, j) => j + 1),
      };
    }
    form.actionSlots = [...form.actionSlots];
  }

  isWeekApplied(form: WishFormModel, index: number, week: number): boolean {
    const slot = form.actionSlots[index];
    return slot.type === 'habit' && slot.weeklyApplied.includes(week);
  }

  toggleWeekApplied(form: WishFormModel, index: number, week: number): void {
    const slot = form.actionSlots[index];
    if (slot.type !== 'habit' || slot.allWeeksApplied) return;
    if (this.isWeekFrozenByEnrollment(form, week)) return;
    const current = [...slot.weeklyApplied];
    const pos = current.indexOf(week);
    if (pos >= 0) {
      current.splice(pos, 1);
    } else {
      current.push(week);
      current.sort((a, b) => a - b);
    }
    form.actionSlots[index] = { ...slot, weeklyApplied: current };
    form.actionSlots = [...form.actionSlots];
  }

  /** Create POST body: one entry per week with text only. */
  private weeklyNotesForPayload(form: WishFormModel): Array<{ week: number; notes: string }> {
    return (form.weeklyNotes ?? []).map((n) => ({
      week: n.week,
      notes: (n.notes ?? '').trim(),
    }));
  }

  onActionSelect(form: WishFormModel, index: number, value: string): void {
    if (value !== 'goal' && value !== 'habit') {
      return;
    }
    if (value === 'goal' && form.isEnrolled) {
      return;
    }
    form.actionSlots[index] = this.createEmptyActionSlot(value);
    if (index === form.actionSlots.length - 1) {
      form.actionSlots.push({ type: 'select' });
    }
    this.normalizeActionSlots(form);
  }

  onDeleteActionSlot(form: WishFormModel, index: number): void {
    form.actionSlots[index] = { type: 'select' };
    this.normalizeActionSlots(form);
  }

  /** Edit modal: lock goal/habit identity fields for actions already on the wish. */
  isEditActionIdentityLocked(
    lockActionIdentity: boolean | undefined,
    slot: WishActionSlot
  ): boolean {
    if (!lockActionIdentity || slot.type === 'select') {
      return false;
    }
    return Boolean((slot.actionId ?? '').toString().trim());
  }

  onHabitSlotHabitChange(form: WishFormModel, index: number): void {
    const slot = form.actionSlots[index];
    if (slot.type !== 'habit') return;
    const hadDescription = (slot.habitJourneyDescription ?? '').trim().length > 0;
    form.actionSlots[index] = {
      ...slot,
      nameId: null,
      habitTime: [''],
      habitDescriptionMismatchWarning: hadDescription,
    };
    form.actionSlots = [...form.actionSlots];
  }

  onHabitSlotCategoryChange(form: WishFormModel, index: number): void {
    const slot = form.actionSlots[index];
    if (slot.type !== 'habit') {
      return;
    }
    const hadDescription = (slot.habitJourneyDescription ?? '').trim().length > 0;
    if (!slot.habitNameId || slot.nameId == null) {
      form.actionSlots[index] = {
        ...slot,
        habitTime: [''],
        habitDescriptionMismatchWarning: hadDescription,
      };
      form.actionSlots = [...form.actionSlots];
      return;
    }
    const first = this.getHabitCategoryTimeFromApi(slot.habitNameId, slot.nameId);
    form.actionSlots[index] = {
      ...slot,
      habitTime: [first],
      habitDescriptionMismatchWarning: hadDescription,
    };
    form.actionSlots = [...form.actionSlots];
  }

  /** Clears the mismatch warning once the member reviews/edits the description themselves. */
  onHabitJourneyDescriptionChange(slot: WishActionSlot): void {
    if (slot.type !== 'habit' || !slot.habitDescriptionMismatchWarning) return;
    slot.habitDescriptionMismatchWarning = false;
  }

  /** Normalize picker/input value to HH:mm and persist on a specific time entry. */
  onHabitTimeChange(form: WishFormModel, slotIndex: number, timeIndex: number, value: string): void {
    const slot = form.actionSlots[slotIndex];
    if (slot.type !== 'habit') {
      return;
    }
    const times = [...slot.habitTime];
    times[timeIndex] = this.firstTimeForTimeInput(value);
    form.actionSlots[slotIndex] = { ...slot, habitTime: times };
    form.actionSlots = [...form.actionSlots];
  }

  addHabitTimeField(form: WishFormModel, slotIndex: number): void {
    const slot = form.actionSlots[slotIndex];
    if (slot.type !== 'habit') return;
    form.actionSlots[slotIndex] = { ...slot, habitTime: [...slot.habitTime, ''] };
    form.actionSlots = [...form.actionSlots];
  }

  removeHabitTimeField(form: WishFormModel, slotIndex: number, timeIndex: number): void {
    const slot = form.actionSlots[slotIndex];
    if (slot.type !== 'habit' || slot.habitTime.length <= 1) return;
    const times = slot.habitTime.filter((_, idx) => idx !== timeIndex);
    form.actionSlots[slotIndex] = { ...slot, habitTime: times };
    form.actionSlots = [...form.actionSlots];
  }

  goalsForWishPillar(pillar: GoalPillarKey | null): WishGoalFieldOption[] {
    if (!pillar) {
      return [];
    }
    return GOALS_BY_PILLAR[pillar] ?? [];
  }

  /** True when another goal slot in the same form already uses this pillar + goal. */
  isWishGoalTaken(
    form: WishFormModel,
    slotIndex: number,
    pillar: GoalPillarKey | null,
    goalFieldKey: string | null
  ): boolean {
    if (!pillar || !goalFieldKey) {
      return false;
    }
    return form.actionSlots.some((slot, i) => {
      if (i === slotIndex || slot.type !== 'goal') {
        return false;
      }
      return slot.goalPillar === pillar && slot.goalFieldKey === goalFieldKey;
    });
  }

  onGoalSlotPillarChange(form: WishFormModel, index: number): void {
    const slot = form.actionSlots[index];
    if (slot.type !== 'goal') {
      return;
    }
    form.actionSlots[index] = { ...slot, goalFieldKey: null, goalValue: '' };
    form.actionSlots = [...form.actionSlots];
  }

  onGoalSlotFieldKeyChange(form: WishFormModel, index: number): void {
    const slot = form.actionSlots[index];
    if (slot.type !== 'goal') {
      return;
    }
    if (this.isWishGoalTaken(form, index, slot.goalPillar, slot.goalFieldKey)) {
      form.actionSlots[index] = { ...slot, goalFieldKey: null, goalValue: '' };
    } else {
      form.actionSlots[index] = { ...slot, goalValue: '' };
    }
    form.actionSlots = [...form.actionSlots];
  }

  onActionFrequencyChange(
    form: WishFormModel,
    index: number,
    value: Frequency | HabitFrequency | ''
  ): void {
    const slot = form.actionSlots[index];
    if (slot.type !== 'goal' && slot.type !== 'habit') {
      return;
    }
    if (value !== 'weekly') {
      form.actionSlots[index] = { ...slot, weeklyWeekdays: [] };
      form.actionSlots = [...form.actionSlots];
    }
  }

  isWeekdaySelected(form: WishFormModel, index: number, day: WeekdayKey): boolean {
    const slot = form.actionSlots[index];
    if (slot.type !== 'goal' && slot.type !== 'habit') {
      return false;
    }
    return (slot.weeklyWeekdays ?? []).includes(day);
  }

  toggleWeekdaySlot(form: WishFormModel, index: number, day: WeekdayKey): void {
    const slot = form.actionSlots[index];
    if (slot.type !== 'goal' && slot.type !== 'habit') {
      return;
    }
    const current = [...(slot.weeklyWeekdays ?? [])];
    const pos = current.indexOf(day);
    if (pos >= 0) {
      current.splice(pos, 1);
    } else {
      current.push(day);
    }
    form.actionSlots[index] = {
      ...slot,
      weeklyWeekdays: this.sortWeekdayKeys(current),
    };
    form.actionSlots = [...form.actionSlots];
  }

  createWish(): void {
    const mid = (this.memberId ?? '').toString().trim();
    if (!mid) {
      this.toast.errorMessage('Member is required.');
      return;
    }
    if (!this.isWishFormComplete(this.createForm, { requireCardForApp: true })) {
      return;
    }

    const payload = this.buildCreateWishApiPayload(this.createForm);
    if (!payload) {
      return;
    }

    this.wishSubmitting = true;
    this.wishesService.createWish(payload).subscribe({
      next: (res) => {
        this.wishSubmitting = false;
        this.toast.successMessage(this.getApiMessage(res) ?? 'Wish created successfully.');
        this.resetCreateWishForm();
      },
      error: (err) => {
        this.wishSubmitting = false;
        this.toast.errorMessage(this.getApiMessage(err) ?? 'Failed to create wish. Please try again.');
      },
    });
  }

  private getApiMessage(res: any): string | null {
    const msg =
      res?.message ??
      res?.result?.message ??
      res?.error?.message ??
      res?.error?.result?.message ??
      res?.error?.title;
    return typeof msg === 'string' && msg.trim() ? msg.trim() : null;
  }

  /**
   * Builds POST body: wishDuration, careTeamNotes, and actions.
   */
  private buildCreateWishApiPayload(form: WishFormModel): object | null {
    const mid = (this.memberId ?? '').toString().trim();
    const weeks = form.wishNumberOfWeeks;
    const cardForApp = form.cardForApp;
    if (!mid || weeks == null || !cardForApp) {
      if (!cardForApp) {
        this.toast.errorMessage('Please select a card for the app.');
      }
      return null;
    }

    const actions: object[] = [];
    for (const s of this.realActionSlots(form)) {
      const action = this.buildActionApiPayload(s);
      if (action === null) {
        return null;
      }
      actions.push(action);
    }

    return {
      memberId: mid,
      wishName: (form.wishName ?? '').trim(),
      wishDescription: (form.wishDescription ?? '').trim(),
      wishDuration: weeks,
      weeklyNotes: this.weeklyNotesForPayload(form),
      careTeamNotes: (form.careTeamNotes ?? '').trim(),
      cardForApp,
      actions,
    };
  }

  private static readonly DELETE_WISH_ACTION_MESSAGE = 'Delete this action';
  private static readonly DELETE_WISH_WEEKLY_NOTE_MESSAGE = 'Delete this note';

  /**
   * PUT body: only top-level fields and actions that changed.
   * Existing actions include `actionId`; new actions use the full create shape.
   * Removed actions send `{ actionId, message: "Delete this action" }`.
   */
  private buildUpdateWishApiPayload(
    current: WishFormModel,
    original: WishFormModel
  ): object | null {
    const weeks = current.wishNumberOfWeeks;
    const originalWeeks = original.wishNumberOfWeeks;
    if (weeks == null) {
      return null;
    }

    const payload: Record<string, unknown> = {};
    const trim = (v: string | undefined) => (v ?? '').trim();

    if (trim(current.wishName) !== trim(original.wishName)) {
      payload['wishName'] = trim(current.wishName);
    }
    if (trim(current.wishDescription) !== trim(original.wishDescription)) {
      payload['wishDescription'] = trim(current.wishDescription);
    }
    if (trim(current.careTeamNotes) !== trim(original.careTeamNotes)) {
      payload['careTeamNotes'] = trim(current.careTeamNotes);
    }
    const weeklyNotesUpdates = this.buildWeeklyNotesUpdatePayload(current, original);
    if (weeklyNotesUpdates.length > 0) {
      payload['weeklyNotes'] = weeklyNotesUpdates;
    }
    if (weeks !== originalWeeks) {
      payload['wishDuration'] = weeks;
    }

    const cardForApp = current.cardForApp;
    if (!cardForApp) {
      this.toast.errorMessage('Please select a card for the app.');
      return null;
    }
    payload['cardForApp'] = cardForApp;

    const originalByActionId = new Map<string, Exclude<WishActionSlot, { type: 'select' }>>();
    for (const s of this.realActionSlots(original)) {
      const id = (s.actionId ?? '').trim();
      if (id) {
        originalByActionId.set(id, s);
      }
    }

    const currentActionIds = new Set<string>();
    const actions: object[] = [];
    for (const slot of this.realActionSlots(current)) {
      const actionId = (slot.actionId ?? '').trim();
      if (actionId) {
        currentActionIds.add(actionId);
      }
      const actionPayload = this.buildActionApiPayload(slot);
      if (actionPayload === null) {
        return null;
      }

      if (!actionId) {
        actions.push(actionPayload);
        continue;
      }

      const originalSlot = originalByActionId.get(actionId);
      if (!originalSlot) {
        actions.push(actionPayload);
        continue;
      }

      const originalPayload = this.buildActionApiPayload(originalSlot);
      if (originalPayload === null) {
        return null;
      }

      const changedFields = this.diffActionPayloads(originalPayload, actionPayload);
      if (Object.keys(changedFields).length > 0) {
        actions.push({ actionId, ...changedFields });
      }
    }

    for (const deletedId of originalByActionId.keys()) {
      if (!currentActionIds.has(deletedId)) {
        actions.push({
          actionId: deletedId,
          message: WishesComponent.DELETE_WISH_ACTION_MESSAGE,
        });
      }
    }

    if (actions.length > 0) {
      payload['actions'] = actions;
    }

    if (Object.keys(payload).length === 0) {
      this.toast.errorMessage('No changes to save.');
      return null;
    }

    return payload;
  }

  /**
   * PUT weeklyNotes: changed notes include `notesId`, `week`, `notes`;
   * removed notes include `notesId`, `week`, `message`; new weeks omit `notesId`.
   */
  private buildWeeklyNotesUpdatePayload(
    current: WishFormModel,
    original: WishFormModel
  ): object[] {
    const trim = (v: string | undefined) => (v ?? '').trim();
    const maxWeek = current.wishNumberOfWeeks ?? 0;
    const currentByWeek = new Map(
      (current.weeklyNotes ?? []).map((n) => [n.week, n] as const)
    );
    const updates: object[] = [];
    const handledNotesIds = new Set<string>();

    for (const orig of original.weeklyNotes ?? []) {
      const notesId = (orig.notesId ?? '').trim();
      if (!notesId) {
        continue;
      }
      const week = orig.week;
      const cur = currentByWeek.get(week);
      const weekStillInRange = week >= 1 && week <= maxWeek;
      const noteStillPresent =
        weekStillInRange && cur != null && (cur.notesId ?? '').trim() === notesId;

      if (!noteStillPresent) {
        updates.push({
          notesId,
          week,
          message: WishesComponent.DELETE_WISH_WEEKLY_NOTE_MESSAGE,
        });
        handledNotesIds.add(notesId);
        continue;
      }

      const origText = trim(orig.notes);
      const curText = trim(cur.notes);
      if (curText !== origText) {
        updates.push({ notesId, week, notes: curText });
      }
      handledNotesIds.add(notesId);
    }

    for (const cur of current.weeklyNotes ?? []) {
      const notesId = (cur.notesId ?? '').trim();
      if (notesId) {
        if (handledNotesIds.has(notesId)) {
          continue;
        }
        const curText = trim(cur.notes);
        if (curText) {
          updates.push({ notesId, week: cur.week, notes: curText });
        }
        continue;
      }
      const curText = trim(cur.notes);
      if (curText) {
        updates.push({ week: cur.week, notes: curText });
      }
    }

    return updates;
  }

  private buildActionApiPayload(
    slot: Exclude<WishActionSlot, { type: 'select' }>
  ): Record<string, unknown> | null {
    if (slot.type === 'goal') {
      return this.buildGoalActionApiPayload(slot);
    }
    return this.buildHabitActionApiPayload(slot);
  }

  private buildGoalActionApiPayload(
    s: Extract<WishActionSlot, { type: 'goal' }>
  ): Record<string, unknown> | null {
    const freq = s.frequency;
    if (!freq) {
      this.toast.errorMessage('Please set frequency for each Goal action.');
      return null;
    }
    if (!s.goalPillar || !s.goalFieldKey) {
      this.toast.errorMessage('Please select Goal Pillar and Goal for each Goal action.');
      return null;
    }
    const goalPillarLabel =
      GOAL_PILLAR_OPTIONS.find((p) => p.value === s.goalPillar)?.label ?? s.goalPillar;
    const goalFieldLabel =
      GOALS_BY_PILLAR[s.goalPillar]?.find((g) => g.key === s.goalFieldKey)?.label ??
      s.goalFieldKey;

    const component = wishGoalFieldComponent(s.goalFieldKey);
    if (!component) {
      this.toast.errorMessage('Unsupported goal for wishes. Sync wish-goal-options with Goals.');
      return null;
    }
    const goalValueRaw = s.goalValue ?? '';
    const goalValueStr = typeof goalValueRaw === 'number' ? String(goalValueRaw) : String(goalValueRaw).trim();
    if (!goalValueStr) {
      this.toast.errorMessage('Please enter a goal value for each Goal action.');
      return null;
    }

    const goal: Record<string, unknown> = {
      component,
      goalPillar: goalPillarLabel,
      goal: goalFieldLabel,
      goalValue: goalValueStr,
      goalUnit: wishGoalFieldUnit(s.goalFieldKey).trim(),
      goalDescription: (s.goalDescription ?? '').trim(),
      goalFrequency: freq,
    };
    if (freq === 'weekly') {
      const wd = this.ensureWeeklyHasWeekdays(s.weeklyWeekdays, 'Goal');
      if (wd === null) {
        return null;
      }
      goal['goalWeekdays'] = wd;
    }
    return goal;
  }

  private buildHabitActionApiPayload(
    s: Extract<WishActionSlot, { type: 'habit' }>
  ): Record<string, unknown> | null {
    const freq = s.frequency;
    if (!freq) {
      this.toast.errorMessage('Please set frequency for each Habit action.');
      return null;
    }
    if (!s.habitNameId || s.nameId == null) {
      this.toast.errorMessage('Please select Habit Name and Category for each Habit action.');
      return null;
    }
    const habitDetails = this.findHabitPredefinedDetails(s.habitNameId, s.nameId);
    if (!habitDetails) {
      this.toast.errorMessage('Could not resolve habit category details. Please re-select habit and category.');
      return null;
    }

    const habitTime = (s.habitTime ?? []).map((t) => t.trim()).filter(Boolean);
    if (habitTime.length === 0) {
      this.toast.errorMessage('Please set time for each Habit action.');
      return null;
    }

    const habit: Record<string, unknown> = {
      nameId: s.nameId,
      title: habitDetails.title,
      Description: habitDetails.description,
      CategoryId: s.habitNameId,
      habitFrequency: freq,
      habitTime,
      habitJourneyDescription: (s.habitJourneyDescription ?? '').trim() || null,
      weeklyApplied: [...s.weeklyApplied].sort((a, b) => a - b),
    };
    if (freq === 'weekly') {
      const wd = this.ensureWeeklyHasWeekdays(s.weeklyWeekdays, 'Habit');
      if (wd === null) {
        return null;
      }
      habit['habitWeekdays'] = wd;
    }
    return habit;
  }

  /** Returns only keys whose values differ between original and current action payloads. */
  private diffActionPayloads(
    original: Record<string, unknown>,
    current: Record<string, unknown>
  ): Record<string, unknown> {
    const diff: Record<string, unknown> = {};
    const keys = new Set([...Object.keys(original), ...Object.keys(current)]);
    for (const key of keys) {
      const origVal = Object.prototype.hasOwnProperty.call(original, key)
        ? original[key]
        : undefined;
      const curVal = Object.prototype.hasOwnProperty.call(current, key)
        ? current[key]
        : undefined;
      if (!this.payloadValuesEqual(origVal, curVal)) {
        diff[key] = curVal;
      }
    }
    return diff;
  }

  private payloadValuesEqual(a: unknown, b: unknown): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  private findHabitPredefinedDetails(
    habitCategoryId: string,
    nameId: number
  ): { title: string; description: string } | null {
    const found = this.findHabitPredefinedEntry(habitCategoryId, nameId);
    if (!found) {
      return null;
    }
    return {
      title: (found.categoryName ?? '').trim(),
      description: (found.description ?? '').trim(),
    };
  }

  private findHabitPredefinedEntry(
    habitCategoryId: string,
    nameId: number
  ): {
    nameId: number;
    categoryName: string;
    description?: string;
    time?: string;
  } | null {
    const habit = this.reminderCategories.find((h) => h?._id === habitCategoryId);
    if (!habit?.subCategories?.length) {
      return null;
    }
    for (const sc of habit.subCategories) {
      const names = sc?.predefinedReminderNames ?? [];
      const found = names.find((n) => n?.nameId === nameId);
      if (found) {
        return found;
      }
    }
    return null;
  }

  private getHabitCategoryTimeFromApi(habitCategoryId: string, nameId: number): string {
    const found = this.findHabitPredefinedEntry(habitCategoryId, nameId);
    return this.firstTimeForTimeInput(found?.time);
  }

  /** First time from API e.g. "08:30,14:30" → "08:30" for input[type=time]. */
  private firstTimeForTimeInput(raw: string | undefined): string {
    const first = (raw ?? '').split(',')[0]?.trim() ?? '';
    if (!first) {
      return '';
    }
    const match = first.match(/^(\d{1,2}):(\d{2})/);
    if (!match) {
      return '';
    }
    const hours = parseInt(match[1], 10);
    const minutes = match[2];
    if (!Number.isFinite(hours) || hours < 0 || hours > 23) {
      return '';
    }
    return `${hours.toString().padStart(2, '0')}:${minutes}`;
  }

  private habitTimeFromApi(a: MemberWishViewAction): string[] {
    const raw = a?.habitTime;
    if (raw == null) return [''];
    const parts = Array.isArray(raw)
      ? raw.map((t) => String(t).trim()).filter(Boolean)
      : String(raw).split(',').map((t) => t.trim()).filter(Boolean);
    const mapped = parts.map((t) => this.firstTimeForTimeInput(t)).filter(Boolean);
    return mapped.length > 0 ? mapped : [''];
  }

  /** Returns sorted weekdays or null if weekly requires at least one day. */
  private ensureWeeklyHasWeekdays(
    weekdays: WeekdayKey[] | undefined,
    actionLabel: string
  ): WeekdayKey[] | null {
    const sorted = this.sortWeekdayKeys(weekdays ?? []);
    if (sorted.length === 0) {
      this.toast.errorMessage(
        `Please select at least one weekday when frequency is Weekly (${actionLabel}).`
      );
      return null;
    }
    return sorted;
  }

  resetCreateWishForm(): void {
    this.createForm = this.emptyWishForm();
  }

  /** Loads a wish template into the create form and switches to the Create wishes tab. */
  useWishTemplate(template: MemberWishView): void {
    const mid = (this.memberId ?? '').toString().trim();
    if (!mid) {
      this.toast.errorMessage('Member is required.');
      return;
    }
    this.applyWishToForm(template, this.createForm);
    this.activeSubTab = 'create';
    this.toast.successMessage('Template loaded. Review the fields, edit if needed, then create the wish.');
  }

  private applyWishToForm(w: MemberWishView, form: WishFormModel): void {
    form.wishName = (w.wishName ?? '').trim();
    form.wishDescription = (w.wishDescription ?? '').trim();
    form.careTeamNotes = (w.careTeamNotes ?? '').trim();
    form.isEnrolled = !!w.isEnrolled;
    form.enrolledDateTime = (w.enrolledDateTime ?? '').toString().trim();
    form.wishNumberOfWeeks = this.parseWishWeeksFromTemplate(w);
    this.syncWeeklyNotesForWeeks(form);
    this.applyWeeklyNotesFromApi(form, w.weeklyNotes);
    const card = (w.cardForApp ?? '').toString().trim();
    form.cardForApp =
      card && (CARD_FOR_APP_OPTIONS as readonly string[]).includes(card)
        ? (card as CardForAppKey)
        : null;

    const slots: WishActionSlot[] = [];
    for (const a of w.actions ?? []) {
      if (this.isViewGoalAction(a)) {
        const slot = this.goalSlotFromViewAction(a, form);
        if (slot) {
          slots.push(slot);
        }
      } else if (this.isViewHabitAction(a)) {
        const slot = this.habitSlotFromViewAction(a, form);
        if (slot) {
          slots.push(slot);
        }
      }
    }

    form.actionSlots = slots.length > 0 ? [...slots, { type: 'select' }] : [{ type: 'select' }];
    if (form.wishNumberOfWeeks != null) {
      this.onWishNumberOfWeeksChange(form);
    }
  }

  private applyWeeklyNotesFromApi(
    form: WishFormModel,
    apiNotes: Array<{ notesId?: string; week?: number; notes?: string }> | undefined
  ): void {
    if (!apiNotes?.length) {
      return;
    }
    const byWeek = new Map<number, { notes: string; notesId?: string }>();
    for (const item of apiNotes) {
      const week =
        typeof item.week === 'number' && Number.isFinite(item.week)
          ? Math.floor(item.week)
          : parseInt(String(item.week ?? '').trim(), 10);
      if (!Number.isFinite(week) || week < 1) {
        continue;
      }
      const notesId = (item.notesId ?? '').toString().trim();
      byWeek.set(week, {
        notes: (item.notes ?? '').toString(),
        notesId: notesId || undefined,
      });
    }
    form.weeklyNotes = (form.weeklyNotes ?? []).map((entry) => {
      const fromApi = byWeek.get(entry.week);
      if (!fromApi) {
        return entry;
      }
      return {
        week: entry.week,
        notes: fromApi.notes,
        notesId: fromApi.notesId ?? entry.notesId,
      };
    });
  }

  private parseWishWeeksFromTemplate(w: MemberWishView): 1 | 2 | 3 | 4 | 5 | 6 | null {
    const fromApi = w?.wishDuration;
    if (fromApi != null && fromApi !== '') {
      const n =
        typeof fromApi === 'number' && Number.isFinite(fromApi)
          ? Math.floor(fromApi)
          : parseInt(String(fromApi).trim(), 10);
      if (Number.isFinite(n) && n >= 1 && n <= 6) {
        return n as 1 | 2 | 3 | 4 | 5 | 6;
      }
    }
    const start = w?.startDate ? new Date(w.startDate) : null;
    const end = w?.endDate ? new Date(w.endDate) : null;
    if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return null;
    }
    const ms = end.getTime() - start.getTime();
    const weeks = Math.max(1, Math.min(6, Math.round(ms / (7 * 24 * 60 * 60 * 1000))));
    return weeks as 1 | 2 | 3 | 4 | 5 | 6;
  }

  private goalSlotFromViewAction(a: MemberWishViewAction, _form: WishFormModel): WishActionSlot | null {
    const pillarLabel = (a.goalPillar ?? '').trim();
    const goalLabel = (a.goal ?? '').trim();
    let pillar = this.resolveGoalPillarKey(pillarLabel);
    let fieldKey = pillar ? this.resolveGoalFieldKey(pillar, goalLabel) : null;
    if (!fieldKey) {
      const resolved = this.resolveGoalFieldKeyAcrossPillars(goalLabel);
      if (resolved) {
        pillar = resolved.pillar;
        fieldKey = resolved.fieldKey;
      }
    }
    if (!pillar || !fieldKey) {
      return null;
    }

    const rawGoalValue = (a.goalValue ?? '').toString().trim();
    const isBedTime = isWishBedTimeGoal(pillar, fieldKey);
    const isFasting = isWishFastingGlucoseGoal(fieldKey);
    let goalValue: string | number = rawGoalValue;
    if (!isBedTime && !isFasting && rawGoalValue !== '') {
      const numVal = Number(rawGoalValue);
      if (Number.isFinite(numVal)) {
        goalValue = numVal;
      }
    }

    const actionId = (a.actionId ?? '').toString().trim();
    return {
      type: 'goal',
      ...(actionId ? { actionId } : {}),
      goalPillar: pillar,
      goalFieldKey: fieldKey,
      goalValue,
      goalDescription: (a.goalDescription ?? '').trim(),
      frequency: this.actionFrequencyFromApi(a),
      weeklyWeekdays: this.weekdayKeysFromApi(a.goalWeekdays ?? a.daysOfWeek),
    };
  }

  private habitSlotFromViewAction(a: MemberWishViewAction, form: WishFormModel): WishActionSlot | null {
    const habitNameId = (a.CategoryId ?? '').toString().trim();
    const nameIdRaw = a.nameId;
    if (!habitNameId || nameIdRaw == null) {
      return null;
    }
    const nameId = typeof nameIdRaw === 'number' ? nameIdRaw : parseInt(String(nameIdRaw), 10);
    if (!Number.isFinite(nameId)) {
      return null;
    }

    const actionId = (a.actionId ?? '').toString().trim();
    const weeklyApplied = Array.isArray(a.weeklyApplied)
      ? a.weeklyApplied.filter((w): w is number => typeof w === 'number' && Number.isFinite(w) && w >= 1)
      : [];
    const totalWeeks = form.wishNumberOfWeeks ?? 0;
    const allWeeksApplied =
      totalWeeks > 0 &&
      weeklyApplied.length >= totalWeeks &&
      Array.from({ length: totalWeeks }, (_, j) => j + 1).every((w) => weeklyApplied.includes(w));
    return {
      type: 'habit',
      ...(actionId ? { actionId } : {}),
      habitNameId,
      nameId,
      frequency: this.habitFrequencyFromApi(a),
      habitTime: this.habitTimeFromApi(a),
      habitJourneyDescription: (a.habitJourneyDescription ?? '').toString().trim(),
      weeklyApplied,
      allWeeksApplied,
      weeklyWeekdays: this.weekdayKeysFromApi(a.habitWeekdays ?? a.daysOfWeek),
    };
  }

  private resolveGoalPillarKey(label: string): GoalPillarKey | null {
    const norm = label.trim().toLowerCase();
    if (!norm) {
      return null;
    }
    for (const p of GOAL_PILLAR_OPTIONS) {
      if (p.label.toLowerCase() === norm || p.value.toLowerCase() === norm) {
        return p.value;
      }
    }
    return null;
  }

  private resolveGoalFieldKey(pillar: GoalPillarKey, label: string): string | null {
    const norm = label.trim().toLowerCase();
    if (!norm) {
      return null;
    }
    const goals = GOALS_BY_PILLAR[pillar] ?? [];
    const found = goals.find(
      (g) => g.label.toLowerCase() === norm || g.key.toLowerCase() === norm
    );
    return found?.key ?? null;
  }

  private resolveGoalFieldKeyAcrossPillars(
    label: string
  ): { pillar: GoalPillarKey; fieldKey: string } | null {
    const norm = label.trim().toLowerCase();
    if (!norm) {
      return null;
    }
    for (const pillar of Object.keys(GOALS_BY_PILLAR) as GoalPillarKey[]) {
      const fieldKey = this.resolveGoalFieldKey(pillar, label);
      if (fieldKey) {
        return { pillar, fieldKey };
      }
    }
    return null;
  }

  private actionFrequencyFromApi(a: MemberWishViewAction): Frequency {
    const raw = (a.goalFrequency ?? a.frequency ?? '').toString().trim().toLowerCase();
    if (raw === 'daily' || raw === 'weekly') {
      return raw;
    }
    return '';
  }

  private habitFrequencyFromApi(a: MemberWishViewAction): HabitFrequency {
    const raw = (a.habitFrequency ?? a.frequency ?? '').toString().trim().toLowerCase();
    const allowed = HABIT_FREQUENCY_OPTIONS.map((o) => o.value);
    return allowed.includes(raw as (typeof allowed)[number])
      ? (raw as HabitFrequency)
      : '';
  }

  private frequencyDisplayLabel(raw: string, isHabit: boolean): string {
    if (!raw) {
      return '—';
    }
    if (isHabit) {
      const opt = HABIT_FREQUENCY_OPTIONS.find((o) => o.value === raw);
      if (opt) {
        return opt.label;
      }
    }
    if (raw === 'weekly') {
      return 'Weekly';
    }
    if (raw === 'daily') {
      return 'Daily';
    }
    return this.capitalizeWord(raw);
  }

  private weekdayKeysFromApi(days: string[] | undefined): WeekdayKey[] {
    if (!days?.length) {
      return [];
    }
    const order = this.weekdayOptions.map((o) => o.key);
    const result: WeekdayKey[] = [];
    for (const d of days) {
      const key = (d ?? '').toString().trim().toLowerCase() as WeekdayKey;
      if (order.includes(key)) {
        result.push(key);
      }
    }
    return this.sortWeekdayKeys(result);
  }

  private createEmptyActionSlot(
    kind: 'goal' | 'habit'
  ): Exclude<WishActionSlot, { type: 'select' }> {
    if (kind === 'goal') {
      return {
        type: 'goal',
        goalPillar: null,
        goalFieldKey: null,
        goalValue: '',
        goalDescription: '',
        frequency: '',
        weeklyWeekdays: [],
      };
    }
    return {
      type: 'habit',
      habitNameId: null,
      nameId: null,
      frequency: '',
      habitTime: [''],
      habitJourneyDescription: '',
      weeklyApplied: [],
      allWeeksApplied: false,
      weeklyWeekdays: [],
    };
  }

  private sortWeekdayKeys(keys: WeekdayKey[]): WeekdayKey[] {
    const order = this.weekdayOptions.map((o) => o.key);
    return [...keys].sort((a, b) => order.indexOf(a) - order.indexOf(b));
  }

  private normalizeActionSlots(form: WishFormModel): void {
    const merged: WishActionSlot[] = [];
    for (const s of form.actionSlots) {
      if (s.type === 'select' && merged.length && merged[merged.length - 1].type === 'select') {
        continue;
      }
      merged.push(s);
    }

    const hasReal = merged.some((s) => s.type !== 'select');
    if (!hasReal) {
      form.actionSlots = [{ type: 'select' }];
      return;
    }

    if (merged[merged.length - 1].type !== 'select') {
      merged.push({ type: 'select' });
    }
    form.actionSlots = merged;
  }

  loadMemberWishes(): void {
    const mid = (this.memberId ?? '').toString().trim();
    if (!mid) {
      this.memberWishes = [];
      this.expandedViewWishKeys.clear();
      return;
    }
    this.isLoadingMemberWishes = true;
    this.wishesService.getMemberWishes(mid).subscribe({
      next: (res) => {
        this.memberWishes = this.normalizeWishesListResponse(res);
        this.expandedViewWishKeys.clear();
        this.isLoadingMemberWishes = false;
      },
      error: (err) => {
        this.memberWishes = [];
        this.expandedViewWishKeys.clear();
        this.isLoadingMemberWishes = false;
        this.toast.errorMessage(this.getApiMessage(err) ?? 'Failed to load wishes.');
      },
    });
  }

  loadMemberWishTemplates(): void {
    const mid = (this.memberId ?? '').toString().trim();
    if (!mid) {
      this.memberWishTemplates = [];
      return;
    }
    this.isLoadingMemberWishTemplates = true;
    this.wishesService.getMemberWishTemplates(mid).subscribe({
      next: (res) => {
        this.memberWishTemplates = this.normalizeTemplatesListResponse(res);
        this.isLoadingMemberWishTemplates = false;
      },
      error: (err) => {
        this.memberWishTemplates = [];
        this.isLoadingMemberWishTemplates = false;
        this.toast.errorMessage(this.getApiMessage(err) ?? 'Failed to load wish templates.');
      },
    });
  }

  private normalizeTemplatesListResponse(res: any): MemberWishView[] {
    if (Array.isArray(res?.templates)) {
      return res.templates as MemberWishView[];
    }
    const wrapped = res?.result ?? res?.data;
    if (Array.isArray(wrapped?.templates)) {
      return wrapped.templates as MemberWishView[];
    }
    return this.normalizeWishesListResponse(res);
  }

  private normalizeWishesListResponse(res: any): MemberWishView[] {
    if (Array.isArray(res?.wishes)) {
      return res.wishes as MemberWishView[];
    }
    const wrapped = res?.result ?? res?.data;
    if (Array.isArray(wrapped?.wishes)) {
      return wrapped.wishes as MemberWishView[];
    }
    const raw = wrapped ?? res;
    if (Array.isArray(raw)) {
      return raw as MemberWishView[];
    }
    if (Array.isArray(raw?.items)) {
      return raw.items as MemberWishView[];
    }
    return [];
  }

  private formatDaysOfWeekList(days: string[] | undefined): string {
    if (!days?.length) {
      return '';
    }
    const order = this.weekdayOptions.map((o) => o.key);
    const sorted = [...days].sort(
      (a, b) => order.indexOf(a?.toLowerCase() as WeekdayKey) - order.indexOf(b?.toLowerCase() as WeekdayKey)
    );
    return sorted
      .map((d) => this.capitalizeWeekdayLabel(d))
      .filter(Boolean)
      .join(', ');
  }

  private capitalizeWeekdayLabel(day: string): string {
    const key = (day ?? '').toLowerCase() as WeekdayKey;
    const found = this.weekdayOptions.find((o) => o.key === key);
    return found?.label ?? this.capitalizeWord(day ?? '');
  }

  private capitalizeWord(s: string): string {
    const t = (s ?? '').trim();
    if (!t) {
      return '';
    }
    return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
  }

  private loadReminderCategories(): void {
    this.isLoadingReminderCategories = true;
    this.reminderService.getReminderCategories().subscribe({
      next: (res) => {
        const normalized = (res as any)?.result ?? res;
        this.reminderCategories = Array.isArray(normalized) ? normalized : [];
        this.isLoadingReminderCategories = false;
      },
      error: () => {
        this.reminderCategories = [];
        this.isLoadingReminderCategories = false;
      },
    });
  }
}
